<template>
    <div class="relative pb-8 -mt-4 z-50">
        <div class="absolute top-0 right-0 flex justify-end items-center">
            <i class="fa fa-search mr-3 text-gray-500"></i>

            <input-text
                class="w-full md:w-64 lg:w-72 xl:w-80"
                input_class="h-10 w-full"
                label_class="text-xs text-gray-600 uppercase tracking-widest inline-block leading-snug font-semibold"
                placeholder="Search all documents"
                v-model="filter"
            />
        </div>

        <div v-if="includes_docusign" class="flex flex-1 mb-6">
            <document-tabs
                current_tab="documents"
                :documents_index_route="documents_index_route"
                :signatures_index_route="signatures_index_route"
            />
        </div>

        <div v-else class="h-12"></div>

        <div class="flex w-full min-h-screen">
            <div class="w-1/3">
                <document-folder-tree
                    :folders="document_folders_proxy"
                    :selected_folder="selected_folder"
                    :has_filter="!! filter"
                    :document_counts="document_counts"
                    :property="property"
                    @click="clearFilter"
                    @selected_folder="selectFolder"
                    @updated_folder="updateFolder"
                    @created_folder="createFolder"
                    @deleted_folder="deleteFolder"
                    @updated_document="updatedDocument"
                />
            </div>

            <div class="w-2/3">
                <div v-if="selected_folder"
                    class="flex items-center text-lg font-semibold pl-8 pt-2"
                >
                    <div class="flex items-center text-blue-600">
                        <folder-icon size="18" class="text-blue-500 mr-2 mb-0.5" />

                        {{ selected_folder.name }}
                    </div>

                    <div @click="editFolder(selected_folder)">
                        <div class="flex items-center font-normal text-xs text-gray-500 hover:text-blue-600 cursor-pointer select-none pl-6 mt-0.5">
                            <i class="far fa-edit mr-1 mb-0.5"></i>

                            Edit
                        </div>
                    </div>

                    <document-folder-edit-modal
                        :show="show_edit_folder_modal"
                        :folder="selected_folder"
                        :property_id="property.id"
                        @closed="editFolderModalClosed"
                        @updatedFolder="updateFolder"
                        @deletedFolder="deleteFolder"
                    />
                </div>

                <div v-if="documents_proxy.length"
                    class="pl-8"
                >
                    <div class="border-t border-gray-400 mt-5 mb-2">
                        <documents-entry v-for="document in documents_to_display"
                            :key="document.id"
                            :document="document"
                            :signatures_enabled="has_active_docusign_user"
                            @edited="documentEdited"
                            @deleted="documentDeleted"
                        />
                    </div>
                </div>

                <div v-if="! read_only" class="flex justify-end pl-8 py-4">
                    <input-file
                        :store_url="store_route"
                        :parameters="{ folder: selected_folder_id }"
                        @uploaded="documentUploaded"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DocumentsEntry from './documents-entry.vue';
import DocumentFolderEditModal from './document-folder-edit-modal.vue';
import DocumentFolderTree from './document-folder-tree.vue';
import DocumentTabs from './document-tabs.vue';

import { orderBy } from 'lodash';
import { FolderIcon, UploadIcon } from 'vue-feather-icons';

export default {
    components: {
        DocumentsEntry,
        DocumentFolderEditModal,
        DocumentFolderTree,
        DocumentTabs,
        FolderIcon,
        UploadIcon,
    },

    props: {
        property: {},
        documents: {},
        document_folders: {},
        read_only: { default: false },
        includes_docusign: { default: false },
        has_active_docusign_user: { default: false },
        docusign_event: {},
        store_route: {},
        documents_index_route: {},
        signatures_index_route: {},
    },

    data() {
        return {
            documents_proxy: this.documents,
            document_folders_proxy: this.document_folders,
            filter: null,
            selected_folder_id: null,
            show_edit_folder_modal: false,
        }
    },

    computed: {
        filtered_documents() {
            return this.filter
                ? this.documents_proxy.filter(document => document.display_filename.toLowerCase().includes(this.filter.toLowerCase()))
                : this.documents_proxy;
        },
        sorted_documents() {
            return orderBy(this.filtered_documents, 'display_filename', 'asc');
        },
        documents_to_display() {
            return this.filter
                ? this.sorted_documents
                : this.sorted_documents.filter(document => document.folder_id == this.selected_folder_id);
        },
        document_counts() {
            return this.documents_proxy.reduce((carry, item) => {
                if (! carry[item.folder_id]) {
                    carry[item.folder_id] = 0;
                }

                carry[item.folder_id]++;

                return carry;
            }, {});
        },
        selected_folder() {
            return this.document_folders_proxy.find(folder => folder.id === this.selected_folder_id);
        },
    },

    methods: {
        getDocuments() {
            axios.get(this.documents_index_route)
                .then(response => {
                    this.documents_proxy = response.data?.data;
                })
                .catch(error => {
                    console.log(error);
                });
        },
        getDocumentById(id) {
            return this.documents.find(document => document.id === parseInt(id));
        },
        documentUploaded(document) {
            this.getDocuments();
        },
        documentDeleted(id) {
            this.getDocuments();
        },
        documentEdited(id) {
            this.getDocuments();
        },
        updatedDocument(document) {
            let index = this.documents_proxy.findIndex(document_proxy => document_proxy.id === document.id);
            this.documents_proxy.splice(index, 1, document);
        },
        selectFolder(id) {
            this.selected_folder_id = id;
        },
        updateFolder(folder) {
            this.document_folders_proxy.find(folder_proxy => folder_proxy.id === folder.id).name = folder.name;
        },
        createFolder(folder) {
            this.document_folders_proxy.push(folder);
        },
        deleteFolder(folder) {
            this.document_folders_proxy.splice(
                this.document_folders_proxy.findIndex(folder_proxy => folder_proxy.id === folder.id), 1
            );
        },
        clearFilter() {
            this.filter = null;
        },
        editFolder() {
            this.show_edit_folder_modal = true;
        },
        editFolderModalClosed() {
            this.show_edit_folder_modal = false;
        },
    },

    created() {
        this.selected_folder_id = this.document_folders.length ? this.document_folders[0]['id'] : null;
    },
}
</script>
